import React, { useState, useEffect, useMemo } from "react";
import { ArrowLeft, TrendingUp, TrendingDown, Target, Shield, Zap, AlertTriangle, BarChart3, PieChart, Activity, Newspaper, Users, ChevronRight, ExternalLink } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea, CartesianGrid } from "recharts";
import StockChat from "../components/StockChat";
import SentimentPanel from "../components/SentimentPanel";
import { StockNewsPanel } from "../components/NewsPulse";
import { getBackendUrl } from "../lib/backendUrl";

const ACTION_COLORS = {
  "Strong Buy": { bg: "bg-emerald-500/20", text: "text-emerald-400", border: "border-emerald-500/30" },
  "Buy": { bg: "bg-green-500/20", text: "text-green-400", border: "border-green-500/30" },
  "Hold": { bg: "bg-amber-500/20", text: "text-amber-400", border: "border-amber-500/30" },
  "Reduce": { bg: "bg-orange-500/20", text: "text-orange-400", border: "border-orange-500/30" },
  "Exit": { bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/30" },
  "Watch": { bg: "bg-blue-500/20", text: "text-blue-400", border: "border-blue-500/30" },
};

export default function StockDetailPage({ symbol, market, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartRange, setChartRange] = useState("1Y");

  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    setError(null);
    const ctrl = new AbortController();
    
    const fetchData = async () => {
      try {
        const response = await fetch(`${getBackendUrl()}/api/stock/${encodeURIComponent(symbol)}/detail${market ? `?market=${encodeURIComponent(market)}` : ""}`, {
          signal: ctrl.signal,
          credentials: "include",
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setData(data);
        setLoading(false);
      } catch (e) {
        if (e.name === "AbortError") return;
        // Filter out browser extension errors
        const msg = e.message || "";
        if (msg.includes("postMessage") || msg.includes("Request object could not be cloned")) {
          console.warn("Browser extension error ignored:", msg);
          // Retry without signal
          try {
            const response = await fetch(`${getBackendUrl()}/api/stock/${encodeURIComponent(symbol)}/detail${market ? `?market=${encodeURIComponent(market)}` : ""}`, {
              credentials: "include",
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            setData(data);
            setLoading(false);
          } catch (retryError) {
            setError(retryError.message);
            setLoading(false);
          }
          return;
        }
        setError(msg);
        setLoading(false);
      }
    };
    
    fetchData();
    return () => ctrl.abort();
  }, [symbol, market]);

  if (loading) return <LoadingSkeleton />;
  if (error || !data) return <div className="p-8 text-center text-red-400">{error || "Failed to load"}</div>;

  const { company, price, chart, zones, snowflake, portfolio, signals, ai_analysis: ai, fundamentals, news, analyst, peers, fair_value, ownership, earnings } = data;
  const actionStyle = ACTION_COLORS[ai?.action] || ACTION_COLORS["Hold"];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-5" data-testid="stock-detail-page">
      {/* Back + Header */}
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-white transition-colors mb-2" data-testid="back-btn">
        <ArrowLeft size={16} /> Back to Portfolio
      </button>

      {/* HERO SECTION */}
      <HeroSection company={company} price={price} ai={ai} actionStyle={actionStyle} portfolio={portfolio} />

      {/* KEY BULLETS */}
      {ai?.key_bullets && (
        <div className="flex gap-2 flex-wrap">
          {ai.key_bullets.map((b, i) => (
            <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[var(--text-secondary)]">{b}</span>
          ))}
        </div>
      )}

      {/* CHART + SNOWFLAKE ROW */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <ChartSection chart={chart} zones={zones} price={price} portfolio={portfolio} chartRange={chartRange} setChartRange={setChartRange} />
        </div>
        <div>
          <SnowflakeChart snowflake={snowflake} />
        </div>
      </div>

      {/* SENTIMENT — Tickertape scorecard + news + MMI */}
      <SentimentPanel symbol={symbol} />

      {/* NEWS & PULSE — Google News / Finnhub per-stock headlines + sentiment */}
      <StockNewsPanel symbol={symbol} country={portfolio?.country || "India"} />

      {/* ACTION BOX + PORTFOLIO CONTEXT */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ActionBox ai={ai} actionStyle={actionStyle} />
        <PortfolioContext portfolio={portfolio} />
      </div>

      {/* SCORE BREAKDOWN */}
      <ScoreBreakdown signals={signals} snowflake={snowflake} fundamentals={fundamentals} portfolio={portfolio} />

      {/* PHASE 2: FUNDAMENTALS + ANALYST + NEWS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <FundamentalsPanel fundamentals={fundamentals} company={company} />
        <AnalystEstimates analyst={analyst} price={price} />
      </div>

      {/* PEER COMPARISON */}
      {peers && peers.length > 0 && <PeerComparison peers={peers} sector={company?.sector} />}

      {/* PHASE 3: FAIR VALUE + OWNERSHIP + EARNINGS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {fair_value && fair_value.mid && <FairValueRange fair_value={fair_value} />}
        {ownership && Object.keys(ownership).length > 0 && <OwnershipBreakdown ownership={ownership} />}
        {earnings && earnings.length > 0 && <EarningsHistory earnings={earnings} />}
      </div>

      {/* NEWS FEED */}
      {news && news.length > 0 && <NewsFeed news={news} symbol={symbol} />}

      {/* AI Chat — moved to bottom of page */}
      <StockChat symbol={symbol} mode="inline" />
    </div>
  );
}

/* ─── HERO SECTION ─────────────────────────────────────────────────── */
function HeroSection({ company, price, ai, actionStyle, portfolio }) {
  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5" data-testid="hero-section">
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>{company.name}</h1>
            <span className={`text-xs px-2.5 py-1 rounded-md font-semibold ${actionStyle.bg} ${actionStyle.text} ${actionStyle.border} border`}>
              {ai?.action || "Hold"}
            </span>
            {ai?.confidence && <span className="text-xs text-[var(--text-muted)] font-mono">{ai.confidence}% confidence</span>}
          </div>
          <div className="flex items-center gap-3 text-sm text-[var(--text-muted)]">
            <span className="font-mono">{company.symbol}</span>
            <span className="text-white/20">|</span>
            <span>{company.sector}</span>
            {company.industry && <><span className="text-white/20">|</span><span>{company.industry}</span></>}
            <span className="text-white/20">|</span>
            <span>{company.exchange}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold font-mono text-white">
            {company.currency === "USD" ? "$" : "₹"}{price.current?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
          <div className={`font-mono text-sm ${price.day_change >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
            {price.day_change >= 0 ? "+" : ""}{price.day_change?.toFixed(2)} ({price.day_change_pct?.toFixed(2)}%)
          </div>
        </div>
      </div>

      {/* Portfolio summary strip */}
      {portfolio.holding && (
        <div className="mt-4 pt-3 border-t border-white/5 flex flex-wrap gap-6 text-xs">
          <Stat label="Position" value={`${portfolio.total_qty} shares`} />
          <Stat label="Avg Cost" value={`₹${portfolio.weighted_avg_price?.toLocaleString()}`} />
          <Stat label="Current Value" value={fmtINR(portfolio.total_current)} />
          <Stat label="P&L" value={fmtINR(portfolio.combined_pnl)} color={portfolio.combined_pnl >= 0} sub={`${portfolio.combined_pnl_pct >= 0 ? "+" : ""}${portfolio.combined_pnl_pct}%`} />
          <Stat label="Weight" value={`${portfolio.portfolio_weight}%`} />
          <Stat label="Sector" value={`${portfolio.sector} (${portfolio.sector_exposure}%)`} warn={portfolio.sector_overexposed} />
          {portfolio.is_duplicate && <Stat label="Accounts" value={`${portfolio.duplicate_accounts} accounts`} warn />}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color, sub, warn }) {
  return (
    <div>
      <div className="text-[var(--text-muted)] uppercase tracking-wider text-[10px] font-semibold">{label}</div>
      <div className={`font-mono text-sm ${warn ? "text-amber-400" : color === true ? "text-[var(--positive)]" : color === false ? "text-[var(--negative)]" : "text-white"}`}>
        {value} {sub && <span className="text-[10px] opacity-70">{sub}</span>}
      </div>
    </div>
  );
}

/* ─── CHART SECTION ────────────────────────────────────────────────── */
function ChartSection({ chart, zones, price, portfolio, chartRange, setChartRange }) {
  const filteredChart = useMemo(() => {
    if (!chart || chart.length === 0) return [];
    const months = chartRange === "3M" ? 3 : chartRange === "6M" ? 6 : 12;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - months);
    return chart.filter(d => new Date(d.date) >= cutoff);
  }, [chart, chartRange]);

  if (!filteredChart.length) return <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-8 text-center text-[var(--text-muted)]">No chart data</div>;

  const minPrice = Math.min(...filteredChart.map(d => d.low || d.close)) * 0.97;
  const maxPrice = Math.max(...filteredChart.map(d => d.high || d.close)) * 1.03;

  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-4" data-testid="chart-section">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold">Price Chart</span>
        <div className="flex gap-1">
          {["3M", "6M", "1Y"].map(r => (
            <button key={r} onClick={() => setChartRange(r)}
              className={`px-2.5 py-1 text-[10px] rounded-md ${chartRange === r ? "bg-cyan-500/20 text-cyan-400" : "text-[var(--text-muted)] hover:text-white"}`}>{r}</button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={360}>
        <AreaChart data={filteredChart} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#00D4FF" stopOpacity={0.15} />
              <stop offset="100%" stopColor="#00D4FF" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="buyZoneGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#22c55e" stopOpacity={0.08} />
              <stop offset="100%" stopColor="#22c55e" stopOpacity={0.03} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" />
          <XAxis dataKey="date" tickFormatter={d => d.slice(5)} tick={{ fontSize: 10, fill: "#666" }} axisLine={false} tickLine={false} />
          <YAxis domain={[minPrice, maxPrice]} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(1)}K` : v.toFixed(0)} tick={{ fontSize: 10, fill: "#666" }} axisLine={false} tickLine={false} width={55} />
          <Tooltip content={<ChartTooltip />} />

          {/* Buy Zone */}
          {zones?.buy_zone && (
            <ReferenceArea y1={zones.buy_zone.low} y2={zones.buy_zone.high} fill="#22c55e" fillOpacity={0.06} stroke="#22c55e" strokeOpacity={0.2} strokeDasharray="4 4" />
          )}
          {/* Reduce Zone */}
          {zones?.reduce_zone && zones.reduce_zone.low < maxPrice && (
            <ReferenceArea y1={zones.reduce_zone.low} y2={Math.min(zones.reduce_zone.high, maxPrice)} fill="#f97316" fillOpacity={0.06} stroke="#f97316" strokeOpacity={0.2} strokeDasharray="4 4" />
          )}

          {/* SMA lines */}
          <Area type="monotone" dataKey="sma20" stroke="#f59e0b" strokeWidth={1} dot={false} fill="none" strokeOpacity={0.5} />
          <Area type="monotone" dataKey="sma50" stroke="#8b5cf6" strokeWidth={1} dot={false} fill="none" strokeOpacity={0.5} />

          {/* Price */}
          <Area type="monotone" dataKey="close" stroke="#00D4FF" strokeWidth={2} fill="url(#priceGrad)" dot={false} />

          {/* Avg Cost Line */}
          {zones?.avg_cost && <ReferenceLine y={zones.avg_cost} stroke="#ef4444" strokeDasharray="6 3" strokeWidth={1.5} label={{ value: `Avg ₹${zones.avg_cost}`, position: "right", fill: "#ef4444", fontSize: 10 }} />}

          {/* Support levels */}
          {zones?.support?.map((s, i) => (
            <ReferenceLine key={`sup-${i}`} y={s} stroke="#22c55e" strokeDasharray="4 2" strokeWidth={0.8} strokeOpacity={0.4} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
      <div className="flex gap-4 mt-2 text-[10px] text-[var(--text-muted)]">
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-cyan-400 inline-block" />Price</span>
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-amber-400 inline-block" />SMA 20</span>
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-purple-400 inline-block" />SMA 50</span>
        {zones?.avg_cost && <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-red-400 inline-block" style={{ borderTop: "1px dashed" }} />Avg Cost</span>}
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-500/20 border border-green-500/30 inline-block rounded-sm" />Buy Zone</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-orange-500/20 border border-orange-500/30 inline-block rounded-sm" />Reduce Zone</span>
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-2.5 text-xs shadow-xl">
      <div className="text-[var(--text-muted)] mb-1">{d.date}</div>
      <div className="font-mono text-white">₹{d.close?.toLocaleString()}</div>
      {d.sma20 && <div className="text-amber-400 font-mono">SMA20: {d.sma20}</div>}
      {d.sma50 && <div className="text-purple-400 font-mono">SMA50: {d.sma50}</div>}
      <div className="text-[var(--text-muted)] font-mono">Vol: {(d.volume / 1000).toFixed(0)}K</div>
    </div>
  );
}

/* ─── SNOWFLAKE / RADAR CHART ──────────────────────────────────────── */
function SnowflakeChart({ snowflake }) {
  if (!snowflake) return null;
  const dims = [
    { key: "value", label: "VALUE", angle: -90 },
    { key: "future", label: "FUTURE", angle: -18 },
    { key: "past", label: "PAST", angle: 54 },
    { key: "health", label: "HEALTH", angle: 126 },
    { key: "dividend", label: "DIVIDEND", angle: 198 },
  ];

  const cx = 140, cy = 140, maxR = 110;

  const getPoint = (angle, radius) => {
    const rad = (angle * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  };

  // Build polygon points for the score shape
  const scorePoints = dims.map(d => {
    const score = snowflake[d.key]?.score || 0;
    const r = (score / 10) * maxR;
    return getPoint(d.angle, r);
  });
  const polygonStr = scorePoints.map(p => `${p.x},${p.y}`).join(" ");

  // Concentric rings
  const rings = [0.25, 0.5, 0.75, 1.0];

  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-4" data-testid="snowflake-chart">
      <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-2">Stock Snapshot</div>
      <svg viewBox="0 0 280 280" className="w-full max-w-[280px] mx-auto">
        {/* Concentric rings */}
        {rings.map((r, i) => (
          <polygon key={i} points={dims.map(d => { const p = getPoint(d.angle, maxR * r); return `${p.x},${p.y}`; }).join(" ")}
            fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={1} />
        ))}
        {/* Axis lines */}
        {dims.map((d, i) => {
          const p = getPoint(d.angle, maxR);
          return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="rgba(255,255,255,0.08)" strokeWidth={1} />;
        })}
        {/* Score polygon */}
        <polygon points={polygonStr} fill="rgba(234,179,8,0.25)" stroke="#EAB308" strokeWidth={2} />
        {/* Score dots */}
        {scorePoints.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={3} fill="#EAB308" />
        ))}
        {/* Labels */}
        {dims.map((d, i) => {
          const p = getPoint(d.angle, maxR + 22);
          const score = snowflake[d.key]?.score || 0;
          return (
            <text key={i} x={p.x} y={p.y} textAnchor="middle" dominantBaseline="middle"
              className="text-[10px] font-bold fill-white/70 tracking-wider">{d.label}
              <tspan x={p.x} dy="12" className="text-[9px] fill-amber-400 font-mono">{score}/10</tspan>
            </text>
          );
        })}
      </svg>
      {/* Dimension details */}
      <div className="space-y-2 mt-3">
        {dims.map(d => {
          const dim = snowflake[d.key];
          if (!dim) return null;
          return (
            <div key={d.key} className="flex items-center justify-between text-xs">
              <span className="text-[var(--text-muted)]">{d.label}</span>
              <div className="flex items-center gap-2">
                <div className="w-16 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-amber-400" style={{ width: `${(dim.score / 10) * 100}%` }} />
                </div>
                <span className="font-mono text-white w-6 text-right">{dim.score}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── ACTION BOX ───────────────────────────────────────────────────── */
function ActionBox({ ai, actionStyle }) {
  if (!ai) return null;
  return (
    <div className={`${actionStyle.bg} border ${actionStyle.border} rounded-lg p-5`} data-testid="action-box">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold">Recommendation</div>
          <div className={`text-xl font-bold ${actionStyle.text}`} style={{ fontFamily: "Chivo" }}>{ai.action}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-[var(--text-muted)]">Confidence</div>
          <div className="font-mono text-lg text-white">{ai.confidence}%</div>
        </div>
      </div>
      {ai.one_liner && <p className="text-sm text-white/80 mb-3 italic">{ai.one_liner}</p>}

      {/* Ranges */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        {ai.buy_range && (
          <div className="bg-black/20 rounded-md p-2.5">
            <div className="text-[10px] text-green-400 uppercase tracking-wider font-semibold">Buy Range</div>
            <div className="font-mono text-sm text-white">₹{ai.buy_range.low?.toLocaleString()} – ₹{ai.buy_range.high?.toLocaleString()}</div>
          </div>
        )}
        {ai.reduce_zone && (
          <div className="bg-black/20 rounded-md p-2.5">
            <div className="text-[10px] text-orange-400 uppercase tracking-wider font-semibold">Reduce Zone</div>
            <div className="font-mono text-sm text-white">₹{ai.reduce_zone.low?.toLocaleString()} – ₹{ai.reduce_zone.high?.toLocaleString()}</div>
          </div>
        )}
      </div>
      {ai.holding_period && <div className="text-xs text-[var(--text-muted)] mb-3">Holding period: <span className="text-white">{ai.holding_period}</span></div>}

      {/* Reasoning */}
      {ai.action_reasoning && (
        <div className="mb-3">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1">Why this recommendation</div>
          <ul className="space-y-0.5">
            {ai.action_reasoning.map((r, i) => <li key={i} className="text-xs text-white/70 flex gap-1.5"><Zap size={10} className="text-cyan-400 flex-shrink-0 mt-0.5" />{r}</li>)}
          </ul>
        </div>
      )}
      {ai.what_could_change && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1">What could change this view</div>
          <ul className="space-y-0.5">
            {ai.what_could_change.map((r, i) => <li key={i} className="text-xs text-white/50 flex gap-1.5"><AlertTriangle size={10} className="text-amber-400 flex-shrink-0 mt-0.5" />{r}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ─── PORTFOLIO CONTEXT ────────────────────────────────────────────── */
function PortfolioContext({ portfolio }) {
  if (!portfolio?.holding) {
    return (
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5" data-testid="portfolio-context">
        <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-3">Portfolio Context</div>
        <div className="text-sm text-[var(--text-muted)]">Not currently held in portfolio</div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5" data-testid="portfolio-context">
      <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-3">Portfolio Context</div>

      {/* Account breakdown */}
      <div className="space-y-2 mb-4">
        {portfolio.accounts.map((a, i) => (
          <div key={i} className="flex items-center justify-between p-2.5 bg-[var(--bg-base)] rounded-md border border-white/5">
            <div>
              <div className="text-xs text-white">{a.account_name}</div>
              <div className="text-[10px] font-mono text-[var(--text-muted)]">{a.quantity} @ ₹{a.avg_price?.toLocaleString()}</div>
            </div>
            <div className="text-right">
              <div className="text-xs font-mono text-white">{fmtINR(a.current_value)}</div>
              <div className={`text-[10px] font-mono ${a.pnl >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
                {a.pnl >= 0 ? "+" : ""}{fmtINR(a.pnl)} ({a.pnl_pct}%)
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary metrics */}
      <div className="grid grid-cols-2 gap-3 text-xs">
        <MetricCard label="Total Position" value={`${portfolio.total_qty} shares`} />
        <MetricCard label="Avg Cost" value={`₹${portfolio.weighted_avg_price?.toLocaleString()}`} />
        <MetricCard label="Combined P&L" value={fmtINR(portfolio.combined_pnl)} color={portfolio.combined_pnl >= 0} sub={`${portfolio.combined_pnl_pct}%`} />
        <MetricCard label="Portfolio Weight" value={`${portfolio.portfolio_weight}%`} warn={portfolio.portfolio_weight > 5} />
        <MetricCard label="Sector Exposure" value={`${portfolio.sector} ${portfolio.sector_exposure}%`} warn={portfolio.sector_overexposed} />
        <MetricCard label="Accounts" value={`${portfolio.duplicate_accounts} account${portfolio.duplicate_accounts > 1 ? "s" : ""}`} warn={portfolio.is_duplicate} />
      </div>
    </div>
  );
}

function MetricCard({ label, value, color, sub, warn }) {
  return (
    <div className="p-2 bg-[var(--bg-base)] rounded-md border border-white/5">
      <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">{label}</div>
      <div className={`font-mono text-sm ${warn ? "text-amber-400" : color === true ? "text-[var(--positive)]" : color === false ? "text-[var(--negative)]" : "text-white"}`}>
        {value} {sub && <span className="text-[10px] opacity-60">{sub}</span>}
      </div>
    </div>
  );
}

/* ─── SCORE BREAKDOWN ──────────────────────────────────────────────── */
function ScoreBreakdown({ signals, snowflake, fundamentals, portfolio }) {
  const blocks = [
    { label: "Technical", icon: Activity, max: 30, score: signals?.tech_score || 0, color: "text-cyan-400",
      points: [
        signals?.rsi ? `RSI: ${signals.rsi} ${signals.rsi < 30 ? "(Oversold)" : signals.rsi > 70 ? "(Overbought)" : "(Neutral)"}` : null,
        signals?.trend ? `Trend: ${signals.trend}` : null,
        signals?.sma_50 ? `SMA50: ₹${signals.sma_50}` : null,
        signals?.volume_ratio ? `Volume ratio: ${signals.volume_ratio}x` : null,
      ].filter(Boolean) },
    { label: "Valuation", icon: BarChart3, max: 10, score: snowflake?.value?.score || 5, color: "text-amber-400",
      points: snowflake?.value?.points || [] },
    { label: "Growth", icon: TrendingUp, max: 10, score: snowflake?.future?.score || 5, color: "text-green-400",
      points: snowflake?.future?.points || [] },
    { label: "Health", icon: Shield, max: 10, score: snowflake?.health?.score || 5, color: "text-blue-400",
      points: snowflake?.health?.points || [] },
    { label: "Past Performance", icon: Target, max: 10, score: snowflake?.past?.score || 5, color: "text-purple-400",
      points: snowflake?.past?.points || [] },
    { label: "Dividend", icon: PieChart, max: 10, score: snowflake?.dividend?.score || 3, color: "text-teal-400",
      points: snowflake?.dividend?.points || [] },
  ];

  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5" data-testid="score-breakdown">
      <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-4">Score Breakdown</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {blocks.map(b => (
          <div key={b.label} className="bg-[var(--bg-base)] border border-white/5 rounded-md p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <b.icon size={14} className={b.color} />
                <span className="text-xs font-semibold text-white">{b.label}</span>
              </div>
              <span className={`font-mono text-sm font-bold ${b.color}`}>{b.score}/{b.max}</span>
            </div>
            <div className="w-full h-1.5 bg-white/10 rounded-full mb-2">
              <div className={`h-full rounded-full`} style={{ width: `${(b.score / b.max) * 100}%`, background: "currentColor" }}>
                <div className={`h-full rounded-full ${b.color.replace("text-", "bg-")}`} style={{ width: "100%" }} />
              </div>
            </div>
            <ul className="space-y-0.5">
              {b.points.map((p, i) => (
                <li key={i} className="text-[11px] text-[var(--text-muted)]">• {p}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── FUNDAMENTALS PANEL ────────────────────────────────────────────── */
function FundamentalsPanel({ fundamentals, company }) {
  if (!fundamentals) return null;
  const fmt = (v, suffix = "") => {
    if (v === null || v === undefined) return "N/A";
    if (typeof v === "number") {
      if (suffix === "%") return `${(v * 100).toFixed(1)}%`;
      if (Math.abs(v) >= 1e9) return `₹${(v / 1e9).toFixed(1)}B`;
      if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(1)}Cr`;
      return v.toFixed(2);
    }
    return String(v);
  };

  const rows = [
    { label: "P/E Ratio", value: fmt(fundamentals.pe_ratio), good: fundamentals.pe_ratio && fundamentals.pe_ratio < 25 },
    { label: "P/B Ratio", value: fmt(fundamentals.pb_ratio), good: fundamentals.pb_ratio && fundamentals.pb_ratio < 3 },
    { label: "EV/EBITDA", value: fmt(fundamentals.ev_ebitda), good: fundamentals.ev_ebitda && fundamentals.ev_ebitda < 15 },
    { label: "ROE", value: fmt(fundamentals.roe, "%"), good: fundamentals.roe && fundamentals.roe > 0.15 },
    { label: "ROA", value: fmt(fundamentals.roa, "%"), good: fundamentals.roa && fundamentals.roa > 0.08 },
    { label: "Profit Margin", value: fmt(fundamentals.profit_margin, "%"), good: fundamentals.profit_margin && fundamentals.profit_margin > 0.10 },
    { label: "Revenue Growth", value: fmt(fundamentals.revenue_growth, "%"), good: fundamentals.revenue_growth && fundamentals.revenue_growth > 0.10 },
    { label: "Earnings Growth", value: fmt(fundamentals.earnings_growth, "%"), good: fundamentals.earnings_growth && fundamentals.earnings_growth > 0.10 },
    { label: "Debt/Equity", value: fundamentals.debt_equity != null ? `${fundamentals.debt_equity.toFixed(0)}%` : "N/A", good: fundamentals.debt_equity && fundamentals.debt_equity < 50 },
    { label: "Current Ratio", value: fmt(fundamentals.current_ratio), good: fundamentals.current_ratio && fundamentals.current_ratio > 1.5 },
    { label: "EPS", value: fundamentals.eps ? `₹${fundamentals.eps.toFixed(1)}` : "N/A" },
    { label: "Div Yield", value: fundamentals.dividend_yield ? `${(fundamentals.dividend_yield > 0.5 ? fundamentals.dividend_yield : fundamentals.dividend_yield * 100).toFixed(2)}%` : "N/A" },
  ];

  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5" data-testid="fundamentals-panel">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 size={16} className="text-amber-400" />
        <span className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold">Key Fundamentals</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {rows.map(r => (
          <div key={r.label} className="p-2 bg-[var(--bg-base)] rounded border border-white/5">
            <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">{r.label}</div>
            <div className={`font-mono text-sm mt-0.5 ${r.good === true ? "text-[var(--positive)]" : r.good === false ? "text-[var(--negative)]" : "text-white"}`}>
              {r.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── ANALYST ESTIMATES ────────────────────────────────────────────── */
function AnalystEstimates({ analyst, price }) {
  if (!analyst || !analyst.target_mean) return null;

  const current = price?.current || analyst.current_price || 0;
  const recLabel = {
    "strong_buy": "Strong Buy", "buy": "Buy", "hold": "Hold",
    "sell": "Sell", "strong_sell": "Strong Sell",
  }[analyst.recommendation] || analyst.recommendation || "N/A";
  const recColor = {
    "strong_buy": "text-emerald-400", "buy": "text-green-400", "hold": "text-amber-400",
    "sell": "text-orange-400", "strong_sell": "text-red-400",
  }[analyst.recommendation] || "text-white";

  // Visual target range bar
  const rangeMin = Math.min(analyst.target_low, current) * 0.95;
  const rangeMax = Math.max(analyst.target_high, current) * 1.02;
  const span = rangeMax - rangeMin || 1;
  const currentPct = ((current - rangeMin) / span) * 100;
  const meanPct = ((analyst.target_mean - rangeMin) / span) * 100;
  const lowPct = ((analyst.target_low - rangeMin) / span) * 100;
  const highPct = ((analyst.target_high - rangeMin) / span) * 100;

  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5" data-testid="analyst-panel">
      <div className="flex items-center gap-2 mb-4">
        <Users size={16} className="text-blue-400" />
        <span className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold">Analyst Estimates</span>
        {analyst.num_analysts > 0 && <span className="text-[10px] text-[var(--text-muted)]">({analyst.num_analysts} analysts)</span>}
      </div>

      {/* Recommendation badge */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] text-[var(--text-muted)] uppercase">Consensus</div>
          <div className={`text-lg font-bold ${recColor}`} style={{ fontFamily: "Chivo" }}>{recLabel}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-[var(--text-muted)] uppercase">Target Upside</div>
          <div className={`font-mono text-lg font-bold ${analyst.upside_pct >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
            {analyst.upside_pct >= 0 ? "+" : ""}{analyst.upside_pct}%
          </div>
        </div>
      </div>

      {/* Target price range bar */}
      <div className="mb-4">
        <div className="relative h-8 bg-[var(--bg-base)] rounded border border-white/5">
          {/* Low-High range fill */}
          <div className="absolute top-1 bottom-1 rounded bg-blue-500/15"
            style={{ left: `${lowPct}%`, width: `${highPct - lowPct}%` }} />
          {/* Mean target marker */}
          <div className="absolute top-0 bottom-0 w-0.5 bg-blue-400"
            style={{ left: `${meanPct}%` }}>
            <div className="absolute -top-5 -translate-x-1/2 text-[9px] font-mono text-blue-400 whitespace-nowrap">
              ₹{analyst.target_mean?.toLocaleString()}
            </div>
          </div>
          {/* Current price marker */}
          <div className="absolute top-0 bottom-0 w-0.5 bg-white"
            style={{ left: `${currentPct}%` }}>
            <div className="absolute -bottom-5 -translate-x-1/2 text-[9px] font-mono text-white whitespace-nowrap">
              ₹{current?.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Target prices grid */}
      <div className="grid grid-cols-3 gap-2 mt-6">
        <div className="p-2 bg-[var(--bg-base)] rounded border border-white/5 text-center">
          <div className="text-[10px] text-red-400 uppercase">Low</div>
          <div className="font-mono text-sm text-white">₹{analyst.target_low?.toLocaleString()}</div>
        </div>
        <div className="p-2 bg-[var(--bg-base)] rounded border border-blue-500/20 text-center">
          <div className="text-[10px] text-blue-400 uppercase">Mean</div>
          <div className="font-mono text-sm text-white font-bold">₹{analyst.target_mean?.toLocaleString()}</div>
        </div>
        <div className="p-2 bg-[var(--bg-base)] rounded border border-white/5 text-center">
          <div className="text-[10px] text-green-400 uppercase">High</div>
          <div className="font-mono text-sm text-white">₹{analyst.target_high?.toLocaleString()}</div>
        </div>
      </div>
    </div>
  );
}

/* ─── PEER COMPARISON ──────────────────────────────────────────────── */
function PeerComparison({ peers, sector }) {
  if (!peers || peers.length === 0) return null;
  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5" data-testid="peer-comparison">
      <div className="flex items-center gap-2 mb-4">
        <Target size={16} className="text-purple-400" />
        <span className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold">vs Sector Average</span>
        {sector && <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[var(--text-muted)]">{sector}</span>}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {peers.map(p => (
          <div key={p.metric} className="p-2.5 bg-[var(--bg-base)] rounded border border-white/5">
            <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">{p.metric}</div>
            <div className="font-mono text-sm text-white">{typeof p.value === 'number' ? p.value : p.value}</div>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-[9px] text-[var(--text-muted)]">Sector: {typeof p.sector_avg === 'number' ? p.sector_avg : p.sector_avg}</span>
            </div>
            <div className={`text-[10px] font-medium mt-0.5 ${
              (p.metric === "Debt/Equity" || p.metric === "P/E Ratio" || p.metric === "P/B Ratio")
                ? (p.verdict === "Below" ? "text-[var(--positive)]" : "text-[var(--negative)]")
                : (p.verdict === "Above" ? "text-[var(--positive)]" : "text-[var(--negative)]")
            }`}>
              {p.verdict} avg
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── NEWS FEED ────────────────────────────────────────────────────── */
function NewsFeed({ news, symbol }) {
  if (!news || news.length === 0) return null;
  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5" data-testid="news-feed">
      <div className="flex items-center gap-2 mb-4">
        <Newspaper size={16} className="text-cyan-400" />
        <span className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold">Recent News</span>
      </div>
      <div className="space-y-2">
        {news.map((n, i) => (
          <a key={i} href={n.link} target="_blank" rel="noopener noreferrer"
            className="flex items-start gap-3 p-3 bg-[var(--bg-base)] rounded border border-white/5 hover:border-white/15 transition-colors group">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white group-hover:text-[var(--brand)] transition-colors line-clamp-2">{n.title}</div>
              {n.summary && <div className="text-xs text-[var(--text-muted)] mt-1 line-clamp-1">{n.summary}</div>}
              <div className="flex items-center gap-2 mt-1.5">
                {n.publisher && <span className="text-[10px] text-[var(--text-secondary)]">{n.publisher}</span>}
                {n.published && <span className="text-[10px] text-[var(--text-muted)]">{n.published}</span>}
              </div>
            </div>
            <ExternalLink size={14} className="text-[var(--text-muted)] flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>
        ))}
      </div>
    </div>
  );
}

/* ─── FAIR VALUE RANGE ──────────────────────────────────────────────── */
function FairValueRange({ fair_value }) {
  if (!fair_value || !fair_value.mid) return null;
  const { low, high, mid, current, upside_pct, verdict, methods } = fair_value;
  const rangeMin = Math.min(low, current) * 0.92;
  const rangeMax = Math.max(high, current) * 1.05;
  const span = rangeMax - rangeMin || 1;
  const currentPct = ((current - rangeMin) / span) * 100;
  const midPct = ((mid - rangeMin) / span) * 100;
  const lowPct = ((low - rangeMin) / span) * 100;
  const highPct = ((high - rangeMin) / span) * 100;
  const verdictColor = verdict === "Undervalued" ? "text-emerald-400" : verdict === "Fair" ? "text-amber-400" : "text-red-400";

  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5" data-testid="fair-value-panel">
      <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-3">Fair Value Estimate</div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className="font-mono text-xl font-bold text-white">{fmtINR(mid)}</span>
          <span className={`ml-2 text-sm font-medium ${verdictColor}`}>{verdict}</span>
        </div>
        <span className={`font-mono text-sm ${upside_pct >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
          {upside_pct >= 0 ? "+" : ""}{upside_pct}%
        </span>
      </div>
      {/* Range bar */}
      <div className="relative h-6 bg-[var(--bg-base)] rounded border border-white/5 mb-3">
        <div className="absolute top-0.5 bottom-0.5 rounded bg-emerald-500/10" style={{ left: `${lowPct}%`, width: `${highPct - lowPct}%` }} />
        <div className="absolute top-0 bottom-0 w-0.5 bg-emerald-400" style={{ left: `${midPct}%` }} />
        <div className="absolute top-0 bottom-0 w-0.5 bg-white" style={{ left: `${currentPct}%` }}>
          <div className="absolute -bottom-4 -translate-x-1/2 text-[8px] font-mono text-white">CMP</div>
        </div>
      </div>
      {/* Methods */}
      <div className="space-y-1 mt-4">
        {(methods || []).map((m, i) => (
          <div key={i} className="flex justify-between text-[11px]">
            <span className="text-[var(--text-muted)]">{m.method}</span>
            <span className="font-mono text-white">{fmtINR(m.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── OWNERSHIP BREAKDOWN ──────────────────────────────────────────── */
function OwnershipBreakdown({ ownership }) {
  if (!ownership || Object.keys(ownership).length === 0) return null;
  const entries = [
    { label: "Promoter", key: "promoter", color: "#007AFF" },
    { label: "Institutions", key: "institutions", color: "#10B981" },
    { label: "Public", key: "public", color: "#F59E0B" },
  ].filter(e => ownership[e.key] != null);

  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5" data-testid="ownership-panel">
      <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-3">Ownership</div>
      <div className="flex h-3 rounded-full overflow-hidden mb-3 bg-white/5">
        {entries.map(e => (
          <div key={e.key} style={{ width: `${ownership[e.key]}%`, background: e.color }} className="h-full" title={`${e.label}: ${ownership[e.key]}%`} />
        ))}
      </div>
      <div className="space-y-2">
        {entries.map(e => (
          <div key={e.key} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: e.color }} />
              <span className="text-xs text-[var(--text-secondary)]">{e.label}</span>
            </div>
            <span className="font-mono text-sm text-white">{ownership[e.key]}%</span>
          </div>
        ))}
        {ownership.float_pct != null && (
          <div className="flex items-center justify-between pt-1 border-t border-white/5">
            <span className="text-xs text-[var(--text-muted)]">Free Float</span>
            <span className="font-mono text-sm text-[var(--text-secondary)]">{ownership.float_pct}%</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── EARNINGS HISTORY ─────────────────────────────────────────────── */
function EarningsHistory({ earnings }) {
  if (!earnings || earnings.length === 0) return null;
  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5" data-testid="earnings-panel">
      <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-3">Earnings History</div>
      <div className="space-y-1.5">
        {earnings.map((e, i) => (
          <div key={i} className="flex items-center justify-between p-2 bg-[var(--bg-base)] rounded border border-white/5">
            <div>
              <div className="text-xs font-mono text-[var(--text-secondary)]">{e.date}</div>
            </div>
            <div className="flex items-center gap-3 text-xs">
              {e.estimated_eps != null && (
                <span className="text-[var(--text-muted)]">Est: <span className="font-mono text-white">{e.estimated_eps}</span></span>
              )}
              {e.actual_eps != null && (
                <span className="text-[var(--text-muted)]">Act: <span className={`font-mono ${e.beat === true ? "text-[var(--positive)]" : e.beat === false ? "text-[var(--negative)]" : "text-white"}`}>{e.actual_eps}</span></span>
              )}
              {e.surprise_pct != null && (
                <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${e.surprise_pct >= 0 ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>
                  {e.surprise_pct >= 0 ? "+" : ""}{e.surprise_pct}%
                </span>
              )}
              {e.beat != null && (
                <span className={`text-[10px] font-bold ${e.beat ? "text-emerald-400" : "text-red-400"}`}>
                  {e.beat ? "BEAT" : "MISS"}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── LOADING SKELETON ─────────────────────────────────────────────── */
function LoadingSkeleton() {
  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-5 animate-pulse">
      <div className="h-4 w-32 bg-white/5 rounded" />
      <div className="h-32 bg-white/5 rounded-lg" />
      <div className="grid grid-cols-3 gap-5">
        <div className="col-span-2 h-96 bg-white/5 rounded-lg" />
        <div className="h-96 bg-white/5 rounded-lg" />
      </div>
      <div className="grid grid-cols-2 gap-5">
        <div className="h-64 bg-white/5 rounded-lg" />
        <div className="h-64 bg-white/5 rounded-lg" />
      </div>
    </div>
  );
}

function fmtINR(v) {
  if (!v && v !== 0) return "--";
  const abs = Math.abs(v);
  if (abs >= 10000000) return `${v < 0 ? "-" : ""}₹${(Math.abs(v) / 10000000).toFixed(2)}Cr`;
  if (abs >= 100000) return `${v < 0 ? "-" : ""}₹${(Math.abs(v) / 100000).toFixed(2)}L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
